/* ============================================================================================
   Product 3D / AR — gallery overlay viewer, driven by the branch_3d_files metaobject.

   Data flow
     - Liquid resolves ONE variant model per render (branch_3d_files['v-<variant ID>'],
       with a guarded SKU fallback for unmigrated entries)
       and embeds its payload in <script data-product-3d-initial> (see snippets/product-3d-payload).
     - On variant change we re-fetch sections/product-3d-availability through the Section Rendering
       API (one lookup per request). Never resolve many entries in one render: Liquid caps metaobject
       handle lookups at 20 unique entries per page.

   Rendering
     - Vendored Google model-viewer 4.3.1 (data-runtime-src), loaded on first open, with PBR-neutral
       tone mapping. Shopify's model-viewer-ui bundle pins v1.12 (2021) and renders these materials
       flat/dark, so it is not used.
     - Storefront hazard: Visually.io wraps window.fetch and silently swallows three.js' GLB fetches
       (root-caused 2026-08-05). installModelFetchBypass() routes ONLY model-CDN requests through a
       pristine fetch captured from a same-origin iframe.
     - Keep the current gallery photo or presented model beneath the PDP loading ring. A hidden
       candidate loads separately, restores the current camera for variant/4K changes, and replaces
       the previous visual together with its background and controls on the next render frame.

   AR
     - "View in your space": iOS → AR Quick Look (rel="ar" anchor to the processed USDZ);
       Android → Scene Viewer intent (processed GLB); desktop → QR popup encoding this PDP with
       ?variant=<id>&ar=1 (+preview_theme_id on unpublished themes). ?ar=1 auto-launches on phones.

   4K
     - "Load in 4K (N MB)" swaps the source to the raw master (entry.master_url) on demand only,
       preserving orbit + field of view and re-aiming at the new bounding-box center (the two files
       may have different origins). No automatic upgrade: no cross-browser metered-connection signal.
     - Successful upgrades are remembered per variant and exact master URL for this page visit.
       Originals download through an abortable deadline, then a temporary Blob URL supplies the
       hidden candidate. The committed viewer is reused on reopen; stable original URLs can
       reuse the browser HTTP cache. No model bytes or preferences are stored in cookies.
   ============================================================================================ */
(function () {
  'use strict';

  var SELECTORS = {
    root: '[data-product-3d]',
    initial: 'script[data-product-3d-initial]',
    control: '[data-product-3d-control]',
    open: '[data-product-3d-open]',
    viewer: '[data-product-3d-viewer]',
    stage: '[data-product-3d-stage]',
    status: '[data-product-3d-status]',
    loading: '[data-product-3d-loading]',
    placeholder: '[data-product-3d-placeholder]',
    unavailable: '[data-product-3d-unavailable]',
    close: '[data-product-3d-close]',
    expand: '[data-product-3d-expand]',
    fullscreen: '[data-product-3d-fullscreen]',
    hd: '[data-product-3d-hd]',
    ar: '[data-product-3d-ar]',
    qr: '[data-product-3d-qr]',
    qrClose: '[data-product-3d-qr-close]',
    qrCode: '[data-product-3d-qr-code]'
  };

  function $(root, selector) { return root.querySelector(selector); }
  function roots() { return Array.prototype.slice.call(document.querySelectorAll(SELECTORS.root)); }
  function absoluteUrl(url) { return url && url.indexOf('//') === 0 ? 'https:' + url : url; }

  // Visually changes this existing theme convention after page load. Missing/invalid
  // assignment stays in control; the server-side theme setting remains the master gate.
  var experimentId = 'product-3d-test';
  // The viewer and configurator run at every width. Only variant 2's automatic opening
  // after an addon selection stays on the desktop layout, so it never takes over a phone.
  var desktopMedia = window.matchMedia && window.matchMedia('(min-width: 900px)');
  function experimentMode() {
    var test = document.getElementById(experimentId);
    if (!test || test.getAttribute('data-test-active') !== 'true') return '0';
    var variant = test.getAttribute('data-test-variant');
    return variant === '1' || variant === '2' ? variant : '0';
  }
  function isEnabled() {
    return experimentMode() !== '0';
  }
  function isDesktopLayout() { return desktopMedia ? desktopMedia.matches : window.innerWidth >= 900; }
  function isAutoOpenEnabled() { return isEnabled() && experimentMode() === '2' && isDesktopLayout(); }

  function hdLoadPolicy(root) {
    var value = (root && root.getAttribute('data-hd-timeout-seconds') || '').trim();
    var seconds = Number(value);
    if (!Number.isFinite(seconds) || !value) seconds = 5;
    return {
      timeoutMs: Math.min(60, Math.max(1, seconds)) * 1000,
      message: (root && root.getAttribute('data-hd-timeout-message') || '').trim() ||
        '4K took too long to load. Your connection may be slow. Please try again.'
    };
  }

  /* Save on the Shopify cart, so navigation, viewer close and variant changes cannot unset it.
     Reassert on a new opening (including a restored tab with a new cart), rather than storing a
     permanent browser flag that could mark future orders without another 3D/AR interaction. */
  var usageTriggered = false;
  var usageDirty = false;
  var usageRequest = null;
  var usageRetryTimer = null;
  var usageRetries = 0;

  function flushUsage() {
    if (usageRequest) return usageRequest;
    if (!usageDirty) return Promise.resolve(usageTriggered);
    window.clearTimeout(usageRetryTimer);
    var routes = window.Shopify && window.Shopify.routes;
    var request;
    try {
      request = window.fetch(((routes && routes.root) || '/') + 'cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true, // Native AR can immediately background the browser.
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: { '3D/AR used': 'Yes' } })
      });
    } catch (error) { request = Promise.reject(error); }
    usageRequest = Promise.resolve(request).then(function (response) {
      if (!response.ok) throw new Error('Cart usage update failed');
      return response.json();
    }).then(function (cart) {
      if (!cart.attributes || cart.attributes['3D/AR used'] !== 'Yes') throw new Error('Cart usage not saved');
      usageDirty = false;
      usageRetries = 0;
      return true;
    }).catch(function () {
      // Tracking never blocks the viewer. Retry transient failures, and allow a later opening or
      // restored connection to try again if both retries fail.
      if (usageRetries < 2) {
        usageRetries++;
        usageRetryTimer = window.setTimeout(flushUsage, usageRetries * 1500);
      }
      return false;
    }).then(function (saved) {
      usageRequest = null;
      return saved;
    });
    return usageRequest;
  }

  function recordUsage() {
    usageTriggered = true;
    usageDirty = true;
    usageRetries = 0;
    flushUsage();
  }

  window.addEventListener('online', function () {
    if (usageDirty) { usageRetries = 0; flushUsage(); }
  });
  window.addEventListener('pagehide', function () { if (usageDirty) flushUsage(); });

  /* ---- state per root ------------------------------------------------------------------------ */
  function state(root) {
    if (!root.__p3d) {
      root.__p3d = { payloads: {}, inflight: {}, pendingVariant: null, current: null, viewer: null,
        presentedViewer: null, presentedLoad: null, hd: 'idle', upgraded: {}, load: null, showRequest: 0 };
      var initial = $(root, SELECTORS.initial);
      if (initial) {
        try {
          var payload = JSON.parse(initial.textContent);
          if (payload && payload.variantId) {
            root.__p3d.payloads[String(payload.variantId)] = payload;
            root.__p3d.current = payload;
          }
        } catch (e) { /* no initial payload */ }
      }
    }
    return root.__p3d;
  }

  /* ---- selected variant --------------------------------------------------------------------- */
  /* The theme keeps the selected variant in a hidden input inside the product form
     (id="product-<productId>-variant", name="id[]"); fall back to ?variant= in the URL. */
  function currentVariantId(root) {
    var input = document.getElementById('product-' + root.getAttribute('data-product-id') + '-variant') ||
      document.querySelector('form[action*="/cart/add"] input[name="id[]"]');
    if (input && input.value) return String(input.value);
    var fromUrl = new URLSearchParams(window.location.search).get('variant');
    return fromUrl ? String(fromUrl) : null;
  }

  /* ---- availability / payload via Section Rendering API ------------------------------------- */
  function availabilityUrl(root, variantId) {
    var base = (root.getAttribute('data-product-url') || window.location.pathname).split('?')[0];
    var section = root.getAttribute('data-availability-section') || 'product-3d-availability';
    return base + '?variant=' + encodeURIComponent(variantId) + '&section_id=' + encodeURIComponent(section);
  }

  function fetchPayload(root, variantId) {
    if (!isEnabled()) return Promise.resolve({ variantId: variantId, available: false, showCta: false });
    var s = state(root);
    if (s.payloads[variantId]) return Promise.resolve(s.payloads[variantId]);
    if (s.inflight[variantId]) return s.inflight[variantId];
    s.inflight[variantId] = fetch(availabilityUrl(root, variantId), { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('availability HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.querySelector('[data-product-3d-availability]');
        var payload = node ? JSON.parse(node.textContent) : null;
        /* Only trust a payload for the variant we asked for (an unknown id falls back to the first
           available variant server-side). */
        if (!payload || String(payload.variantId) !== String(variantId)) throw new Error('availability returned no matching variant');
        s.payloads[variantId] = payload;
        return payload;
      })
      .catch(function (error) {
        if (window.console && console.warn) console.warn('[product-3d]', error);
        return { variantId: variantId, available: false, availabilityError: true };
      })
      .then(function (payload) { delete s.inflight[variantId]; return payload; });
    return s.inflight[variantId];
  }

  /* PDP CTA gate: the entry must exist AND not be an accessory (role from the metaobject; headrests and
     desk accessories carry a model for the configurator but never show a PDP CTA). Older payloads
     without showCta fall back to `available`. */
  function ctaAllowed(payload) {
    return !!(payload && payload.available && payload.showCta !== false);
  }

  function applyPayload(root, payload) {
    if (!isEnabled()) return;
    var s = state(root);
    var wasOpen = root.getAttribute('data-open') === 'true';
    clearUnavailable(root);
    s.current = payload;
    root.setAttribute('data-variant-id', String(payload.variantId));
    updateVisibility(root);

    var cta = $(root, SELECTORS.open);
    if (cta) cta.setAttribute('aria-expanded', root.getAttribute('data-open') === 'true' ? 'true' : 'false');

    if (wasOpen) {
      if (ctaAllowed(payload)) showModel(root, payload);
      else if (payload.available || payload.availabilityError) closeViewer(root, false); // accessory role, or a failed fetch: close quietly
      else selectFallbackImage(root, String(payload.variantId), function () {
        closeViewer(root, false);
        showUnavailable(root, payload);
      });
    }
  }

  function sync(root) {
    if (!isEnabled()) { disableRoot(root); return; }
    var variantId = currentVariantId(root);
    var s = state(root);
    observeGalleryReadiness(root);
    updateVisibility(root);
    // Returning A -> B -> A must invalidate B even while A is still the rendered variant.
    var changed = s.pendingVariant !== variantId;
    if (changed) {
      if (s.load && s.load.isHd && s.load !== s.presentedLoad &&
          String(s.load.payload.variantId) !== variantId) cancelHdCandidate(root);
      if (s.autoOpen && s.autoOpen.variantId !== variantId) s.autoOpen = null;
      s.selectionStartedAt = Date.now();
      clearUnavailable(root);
      if (root.getAttribute('data-open') === 'true') {
        // Invalidate accessory/master work immediately, before the availability request returns.
        // An old completion must not clear the loader or restore pins for a new selection.
        var cfg = window.BranchConfigurator && window.BranchConfigurator.get(root);
        if (cfg) cfg.begin();
        else setLoading(root, true);
      }
    }
    s.pendingVariant = variantId;
    if (!variantId) return;
    if (variantId === root.getAttribute('data-variant-id')) {
      if (changed && root.getAttribute('data-open') === 'true' && s.current && s.current.available) showModel(root, s.current);
      tryAutoOpen(root);
      return;
    }
    fetchPayload(root, variantId).then(function (payload) {
      if (s.pendingVariant !== variantId || currentVariantId(root) !== variantId) return;
      if (root.getAttribute('data-variant-id') === variantId) return;
      applyPayload(root, payload);
      tryAutoOpen(root);
    });
  }
  function syncAll() { roots().forEach(sync); }

  function galleryReady(root) {
    var gallery = root.closest('.main-product-gallery');
    var outer = gallery && $(gallery, '.main-product-gallery__outer-container');
    if (!outer || outer.getAttribute('data-horizontal-thumbnails-initialized') !== 'true' ||
        outer.getAttribute('data-horizontal-thumbnails-display-stand-in') === 'true') return false;
    var slide = $(outer, '.main-product-gallery__item.slideVisible[data-actual-current-slide="true"]') ||
      $(outer, '.main-product-gallery__item.slideVisible.swiper-slide-active') || $(outer, '.main-product-gallery__item.slideVisible');
    if (!slide || slide.classList.contains('main-product-gallery__item--stub')) return false;
    if (slide.classList.contains('is-video') || slide.classList.contains('is-external-video')) return true;
    return Array.prototype.some.call(slide.querySelectorAll('.main-product__main-product-image'), function (image) {
      return image.complete && image.naturalWidth > 0 && !image.hidden &&
        (!image.getClientRects || image.getClientRects().length > 0);
    });
  }

  /* The 3D/AR CTA belongs to the opening images of the selected variant's gallery only: a lifestyle
     image followed by two renders. Positions count the variant's displayed slides in gallery order;
     the gallery marks the one in view with data-actual-current-slide on every slide change. */
  var CTA_IMAGE_LIMIT = 3;
  function displayed(element) { return !element.getClientRects || element.getClientRects().length > 0; }
  function currentImagePosition(root) {
    var gallery = root.closest('.main-product-gallery');
    var outer = gallery && $(gallery, '.main-product-gallery__outer-container');
    if (!outer) return 0;
    var current = $(outer, '.main-product-gallery__item.slideVisible[data-actual-current-slide="true"]') ||
      $(outer, '.main-product-gallery__item.slideVisible.swiper-slide-active');
    if (!current) return 0;
    var index = Array.prototype.filter.call(outer.querySelectorAll('.main-product-gallery__item.slideVisible'), displayed).indexOf(current);
    return index < 0 ? 0 : index;
  }

  function observeGalleryReadiness(root) {
    var s = state(root), gallery = root.closest('.main-product-gallery');
    var outer = gallery && $(gallery, '.main-product-gallery__outer-container');
    if (!outer || s.galleryReadinessOuter === outer) return;
    if (s.galleryReadinessObserver) s.galleryReadinessObserver.disconnect();
    if (s.galleryReadinessOuter) {
      s.galleryReadinessOuter.removeEventListener('load', s.refreshGalleryVisibility, true);
      s.galleryReadinessOuter.removeEventListener('error', s.refreshGalleryVisibility, true);
    }
    s.galleryReadinessOuter = outer;
    s.refreshGalleryVisibility = function () { updateVisibility(root); tryAutoOpen(root); };
    s.galleryReadinessObserver = new MutationObserver(s.refreshGalleryVisibility);
    s.galleryReadinessObserver.observe(outer, { childList: true, subtree: true, attributes: true,
      attributeFilter: ['data-horizontal-thumbnails-initialized', 'data-horizontal-thumbnails-display-stand-in',
        'data-actual-current-slide', 'class', 'src', 'srcset'] });
    outer.addEventListener('load', s.refreshGalleryVisibility, true);
    outer.addEventListener('error', s.refreshGalleryVisibility, true);
  }

  function updateVisibility(root) {
    var allowed = isEnabled();
    root.setAttribute('data-feature-enabled', allowed ? 'true' : 'false');
    var payload = state(root).current, available = allowed && ctaAllowed(payload);
    var selectedVariant = currentVariantId(root);
    var ready = allowed && galleryReady(root);
    var showControl = available && (!selectedVariant || selectedVariant === String(payload.variantId)) && ready &&
      currentImagePosition(root) < CTA_IMAGE_LIMIT;
    root.setAttribute('data-gallery-ready', ready ? 'true' : 'false');
    var control = $(root, SELECTORS.control);
    var notice = $(root, SELECTORS.unavailable);
    if (control) {
      if (showControl) control.removeAttribute('hidden'); else control.setAttribute('hidden', '');
    }
    if (allowed && (available || root.getAttribute('data-open') === 'true' || (notice && !notice.hasAttribute('hidden')))) root.removeAttribute('hidden');
    else root.setAttribute('hidden', '');
  }

  function clearUnavailable(root) {
    var s = state(root);
    window.clearTimeout(s.noticeTimer);
    window.clearTimeout(s.noticeFadeTimer);
    if (s.cancelFallback) s.cancelFallback();
    var notice = $(root, SELECTORS.unavailable);
    if (notice) {
      notice.setAttribute('hidden', '');
      notice.removeAttribute('data-visible');
    }
    updateVisibility(root);
  }

  function selectFallbackImage(root, variantId, reveal) {
    var gallery = root.closest('.main-product-gallery');
    var outer = gallery && $(gallery, '.main-product-gallery__outer-container');
    if (!outer) return;
    var s = state(root);
    var observer;
    var timeout;
    var attempt = 0;
    var cancelled = false;
    function cancel() {
      cancelled = true;
      if (observer) observer.disconnect();
      window.clearTimeout(timeout);
      gallery.removeEventListener('pointerdown', interact);
      gallery.removeEventListener('keydown', interact);
      if (s.cancelFallback === cancel) s.cancelFallback = null;
    }
    function valid() {
      return !cancelled && currentVariantId(root) === variantId && root.getAttribute('data-open') === 'true';
    }
    function ready() {
      return outer.getAttribute('data-horizontal-thumbnails-initialized') === 'true' &&
        outer.getAttribute('data-horizontal-thumbnails-display-stand-in') !== 'true';
    }
    function fail() {
      if (!valid()) { cancel(); return; }
      cancel();
      setLoading(root, false);
      setStatus(root, 'The product image could not be loaded.');
    }
    function interact(event) {
      // Model gestures can continue while the gallery is covered. Using its thumbnails or
      // arrows instead is an explicit choice to return to normal gallery navigation.
      if (root.contains(event.target)) return;
      cancel();
      closeViewer(root, false);
    }
    function select() {
      if (!valid()) { cancel(); return; }
      var request = ++attempt;
      // Variant restoration owns the gallery until this flag is true. Selecting earlier can
      // be overwritten by its delayed thumbnail callbacks, even when the payload is cached.
      if (!ready()) return;
      var api = window.Branch && window.Branch.productGallery;
      var swiper = api && api.mainProductGallerySwiper;
      if (!swiper || swiper.destroyed || !gallery.contains(swiper.el)) return;
      var media = Array.prototype.slice.call(outer.querySelectorAll('.main-product-gallery__item.slideVisible'));
      var images = media.filter(function (slide) {
        return !slide.classList.contains('is-video') && !slide.classList.contains('is-external-video');
      });
      var target = images[1] || images[0];
      if (!target) { fail(); return; }
      var index = media.indexOf(target);
      api.loadGalleryImage(target);
      swiper.update();
      api._programmaticSlide = true;
      swiper.slideTo(index, 0);
      api.updateThumbnailActiveState(index);

      // loadGalleryImage promotes src/srcset synchronously, but the browser can still be
      // painting a placeholder. Keep 3D covering the gallery until the actual images decode.
      var mainImages = Array.prototype.slice.call(target.querySelectorAll('.main-product__main-product-image'));
      if (!mainImages.length) { fail(); return; }
      var sources = mainImages.map(function (img) { return img.getAttribute('src') + '|' + img.getAttribute('srcset'); });
      function sourcesChanged() {
        return mainImages.some(function (img, i) {
          return sources[i] !== img.getAttribute('src') + '|' + img.getAttribute('srcset');
        });
      }
      window.clearTimeout(timeout);
      timeout = window.setTimeout(fail, 10000);
      Promise.all(mainImages.map(function (img) {
        img.setAttribute('loading', 'eager');
        return img.decode();
      })).then(function () {
        window.requestAnimationFrame(function () {
          if (!valid() || request !== attempt) return;
          if (!ready() || swiper !== api.mainProductGallerySwiper || !target.classList.contains('slideVisible')) return;
          if (sourcesChanged()) { select(); return; }
          // Decode may outlast other gallery callbacks. Reassert the destination in the same
          // frame that removes the cover, so the previous photo cannot appear between them.
          swiper.update();
          if (swiper.activeIndex !== index) {
            api._programmaticSlide = true;
            swiper.slideTo(index, 0);
            api.updateThumbnailActiveState(index);
          }
          cancel();
          reveal();
        });
      }, function () {
        if (valid() && request === attempt) {
          if (sourcesChanged()) select(); else fail();
        }
      });
    }
    s.cancelFallback = cancel;
    gallery.addEventListener('pointerdown', interact);
    gallery.addEventListener('keydown', interact);
    observer = new MutationObserver(select);
    observer.observe(outer, { attributes: true, attributeFilter: ['data-horizontal-thumbnails-initialized', 'data-horizontal-thumbnails-display-stand-in'] });
    timeout = window.setTimeout(fail, 2000);
    select();
  }

  function showUnavailable(root, payload) {
    var s = state(root);
    var notice = $(root, SELECTORS.unavailable);
    var remaining = Math.max(0, 5000 - (Date.now() - s.selectionStartedAt));
    if (!notice || !remaining) return;
    notice.removeAttribute('hidden');
    notice.setAttribute('data-visible', 'true');
    updateVisibility(root);
    s.noticeTimer = window.setTimeout(function () {
      notice.removeAttribute('data-visible');
      s.noticeFadeTimer = window.setTimeout(function () {
        notice.setAttribute('hidden', '');
        updateVisibility(root);
      }, 400);
    }, remaining);
  }

  /* ---- Visually.io fetch bypass ------------------------------------------------------------- */
  var bypassInstalled = false;
  var extraMatchers = [];
  var ownedModelBlobUrls = new Set();
  function isModelRequest(input) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    if (ownedModelBlobUrls.has(url)) return true;
    if (url.indexOf('cdn.shopify.com/3d/models/') !== -1 || url.indexOf('/cdn/shop/3d/models/') !== -1 || /\.glb([?#]|$)/i.test(url)) return true;
    for (var i = 0; i < extraMatchers.length; i++) { if (extraMatchers[i].test(url)) return true; }
    return false;
  }
  function installModelFetchBypass() {
    if (bypassInstalled) return;
    bypassInstalled = true;
    try {
      if (String(window.fetch).indexOf('[native code]') !== -1) return; // nothing wrapped it
      var frame = document.createElement('iframe');
      frame.style.display = 'none';
      frame.setAttribute('aria-hidden', 'true');
      (document.body || document.documentElement).appendChild(frame);
      var nativeFetch = frame.contentWindow.fetch.bind(window);
      frame.remove();
      var wrappedFetch = window.fetch;
      window.fetch = function (input) {
        return isModelRequest(input) ? nativeFetch.apply(null, arguments) : wrappedFetch.apply(this, arguments);
      };
    } catch (e) {
      console.error('[product-3d] model fetch bypass failed to install', e);
    }
  }

  /* ---- runtime ------------------------------------------------------------------------------- */
  var runtimePromise = null;
  function loadRuntime(root) {
    if (runtimePromise) return runtimePromise;
    runtimePromise = new Promise(function (resolve, reject) {
      if (window.customElements.get('model-viewer')) { resolve(); return; }
      var script = document.createElement('script');
      script.type = 'module';
      script.src = root.getAttribute('data-runtime-src') || '';
      script.onerror = function () { reject(new Error('[product-3d] model-viewer runtime failed to load')); };
      document.head.appendChild(script);
      window.customElements.whenDefined('model-viewer').then(resolve, reject);
    });
    return runtimePromise;
  }

  /* ---- viewer -------------------------------------------------------------------------------- */
  function setStatus(root, text, duration) {
    var status = $(root, SELECTORS.status);
    if (!status) return;
    window.clearTimeout(status.__p3dMessageTimer);
    status.__p3dMessageTimer = null;
    if (text) {
      window.clearTimeout(status.__p3dHideTimer);
      status.__p3dHideTimer = null;
      status.removeAttribute('data-dismissing');
      status.textContent = text;
      status.removeAttribute('hidden');
      if (duration > 0) status.__p3dMessageTimer = window.setTimeout(function () { setStatus(root, ''); }, duration);
      return;
    }
    if (status.hasAttribute('data-dismissing')) return;
    function hide() {
      status.__p3dHideTimer = null;
      status.setAttribute('hidden', '');
      status.textContent = '';
      status.removeAttribute('data-dismissing');
    }
    if (status.hasAttribute('hidden') || !status.textContent ||
        (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
      hide();
      return;
    }
    status.setAttribute('data-dismissing', 'true');
    status.__p3dHideTimer = window.setTimeout(hide, 250);
  }

  function actionsReady(root) {
    if (!isEnabled()) return false;
    var s = state(root);
    var shell = $(root, SELECTORS.viewer);
    var presented = s.presentedLoad;
    var cfg = window.BranchConfigurator && window.BranchConfigurator.get(root);
    var modelReady = cfg ? cfg.isReady(s.current) :
      presented && presented.loaded && !presented.failed &&
      String(presented.payload.variantId) === String(s.current && s.current.variantId);
    return !!(shell && root.getAttribute('data-open') === 'true' &&
      shell.getAttribute('data-presented') === 'true' && shell.getAttribute('data-loading') !== 'true' &&
      modelReady && ctaAllowed(s.current) &&
      currentVariantId(root) === String(s.current.variantId));
  }

  function refreshActions(root) {
    var shell = $(root, SELECTORS.viewer);
    if (!shell) return;
    if (actionsReady(root)) shell.setAttribute('data-actions-ready', 'true');
    else shell.removeAttribute('data-actions-ready');
    var cfg = window.BranchConfigurator && window.BranchConfigurator.get(root);
    var hasAccessories = !!(cfg && cfg.getPlacementCount());
    if (!canLoadHd()) {
      var hdButton = $(root, SELECTORS.hd);
      if (hdButton) hdButton.setAttribute('hidden', '');
    }
    var qrNotice = root.__p3dQr && $(root.__p3dQr, '[data-product-3d-ar-notice]');
    if (qrNotice) qrNotice.hidden = !hasAccessories;
  }

  function setLoading(root, loading) {
    var shell = $(root, SELECTORS.viewer);
    var indicator = $(root, SELECTORS.loading);
    shell.setAttribute('aria-busy', loading ? 'true' : 'false');
    if (loading) {
      shell.setAttribute('data-loading', 'true');
      if (indicator) indicator.removeAttribute('hidden');
      setStatus(root, '');
    } else {
      shell.removeAttribute('data-loading');
      if (indicator) indicator.setAttribute('hidden', '');
    }
    // A failed candidate can leave a different variant displayed. Its 4K/AR actions must not
    // operate on the newly selected variant until that variant has actually been presented.
    refreshActions(root);
  }

  function captureGalleryImage(root) {
    var placeholder = $(root, SELECTORS.placeholder);
    if (!placeholder) return;
    placeholder.replaceChildren();
    var gallery = root.closest('.main-product-gallery');
    var outer = gallery && $(gallery, '.main-product-gallery__outer-container');
    if (!outer) return;
    var api = window.Branch && window.Branch.productGallery;
    var swiper = api && api.mainProductGallerySwiper;
    var slides = outer.querySelectorAll('.main-product-gallery__item.slideVisible');
    var slide = swiper && gallery.contains(swiper.el) ? slides[swiper.activeIndex] : slides[0];
    var image = slide && $(slide, '.main-product-gallery__image-inner-container');
    if (!image) return;
    var copy = image.cloneNode(true);
    copy.removeAttribute('id');
    Array.prototype.forEach.call(copy.querySelectorAll('[id]'), function (node) { node.removeAttribute('id'); });
    placeholder.appendChild(copy);
  }

  /* Phones: while the viewer covers the gallery, the gallery slides drop their image sources (they
     keep their size, and the placeholder copy above is not touched) and get them back when it
     closes. The 3D scene, a pin's quick view and the gallery's full-size images used to sit in
     memory together, the moment that killed iPhone tabs. A source set by the gallery meanwhile
     (a color change in 3D mode) wins over the saved one. */
  var phoneLayout = window.matchMedia ? window.matchMedia('(max-width: 899px)') : null;
  function releaseGalleryImages(root, release) {
    var gallery = root.closest('.main-product-gallery');
    var outer = gallery && $(gallery, '.main-product-gallery__outer-container');
    if (!outer || (release && !(phoneLayout && phoneLayout.matches))) return;
    if (release) {
      // Off in a browser with localStorage.setItem('branch:p3d-release-gallery', 'off').
      try { if (window.localStorage.getItem('branch:p3d-release-gallery') === 'off') return; } catch (e) {}
    }
    // Restoring covers the whole gallery: a thumbnail rebuilt from a parked slide while the viewer
    // was open (an add-on chosen from a pin) carries the parked sources too.
    var images = release ? outer.querySelectorAll('.main-product-gallery__item img') : gallery.querySelectorAll('img[data-p3d-src]');
    Array.prototype.forEach.call(images, function (img) {
      if (root.contains(img)) return;
      if (release) {
        if (img.hasAttribute('data-p3d-src') || !img.getAttribute('src')) return;
        img.setAttribute('data-p3d-src', img.getAttribute('src'));
        if (img.hasAttribute('srcset')) img.setAttribute('data-p3d-srcset', img.getAttribute('srcset'));
        img.removeAttribute('srcset');
        img.removeAttribute('src');
      } else if (img.hasAttribute('data-p3d-src')) {
        if (!img.getAttribute('src')) {
          if (img.hasAttribute('data-p3d-srcset')) img.setAttribute('srcset', img.getAttribute('data-p3d-srcset'));
          img.setAttribute('src', img.getAttribute('data-p3d-src'));
        }
        img.removeAttribute('data-p3d-src');
        img.removeAttribute('data-p3d-srcset');
      }
    });
  }

  function presentViewer(root, viewer, load) {
    var s = state(root);
    function commit() {
      load.presenting = false;
      if (!isEnabled() || s.viewer !== viewer || s.load !== load || !load.loaded || load.failed || load.cancelled ||
          root.getAttribute('data-open') !== 'true' || currentVariantId(root) !== String(load.payload.variantId)) return;
      // Parsing can occupy the main thread past the deadline, ahead of its queued timer.
      if (load.isHd && !load.committed && Date.now() >= load.expiresAt) { load.onTimeout(); return; }
      window.clearTimeout(load.hdTimer);
      load.hdTimer = null;
      load.committed = true;
      var shell = $(root, SELECTORS.viewer);
      var firstPresentation = !shell.hasAttribute('data-presented');
      if (s.fullscreen && s.fullscreen.entry && s.fullscreen.entry.viewer !== viewer) clearFullscreenEntry(s.fullscreen);
      if (s.presentedViewer && s.presentedViewer !== viewer) releaseViewer(s.presentedViewer, s.presentedLoad);
      s.presentedViewer = viewer;
      s.presentedLoad = load;
      viewer.removeAttribute('data-pending');
      viewer.removeAttribute('inert');
      viewer.removeAttribute('aria-hidden');
      shell.setAttribute('data-background', load.payload.darkBackground === true ? 'dark' : 'light');
      shell.setAttribute('data-presented', 'true');
      var placeholder = $(root, SELECTORS.placeholder);
      if (placeholder) placeholder.replaceChildren();
      setLoading(root, false);
      var failureNotice = load.failureNotice;
      delete load.failureNotice;
      if (failureNotice) setStatus(root, failureNotice.message, failureNotice.duration);
      else setStatus(root, '');
      s.hd = load.isHd ? 'done' : 'idle';
      if (load.isHd) s.upgraded[String(load.payload.variantId)] = load.payload.masterUrl;
      updateHdButton(root, load.payload);
      if (firstPresentation) flashHints(root);
    }
    if (s.presentedViewer === viewer) { commit(); return; }
    if (load.presenting) return;
    load.presenting = true;
    window.requestAnimationFrame(commit);
  }

  function formatMb(bytes) {
    var mb = bytes / 1e6;
    return (mb >= 10 ? Math.round(mb) : mb.toFixed(1)) + 'MB';
  }

  // Phones and tablets keep Shopify's processed model, including in landscape or desktop mode.
  // The viewport branch also keeps the mobile layout free of the memory-heavy upgrade.
  var mobileHdQuery = '(any-pointer: coarse), (max-width: 767px)';
  function canLoadHd() {
    return isEnabled() && (!window.matchMedia || !window.matchMedia(mobileHdQuery).matches);
  }

  function updateHdButton(root, payload) {
    var s = state(root);
    var button = $(root, SELECTORS.hd);
    if (!button) return;
    if (!canLoadHd() || !payload || !payload.masterUrl || s.upgraded[String(payload.variantId)] === payload.masterUrl) {
      button.setAttribute('hidden', ''); return;
    }
    var label = 'Load in 4K' + (payload.masterBytes ? ' (' + formatMb(Number(payload.masterBytes)) + ')' : '');
    button.textContent = s.hd === 'loading' ? 'Loading 4K…' : label;
    button.disabled = s.hd === 'loading';
    button.removeAttribute('hidden');
  }

  function releaseViewer(viewer, load) {
    if (load) {
      load.cancelled = true;
      window.clearTimeout(load.hdTimer);
      load.hdTimer = null;
      if (load.controller) load.controller.abort();
      if (load.objectUrl) {
        ownedModelBlobUrls.delete(load.objectUrl);
        window.URL.revokeObjectURL(load.objectUrl);
        load.objectUrl = null;
      }
    }
    if (viewer) viewer.remove();
  }

  function cancelHdCandidate(root) {
    var s = state(root);
    if (!s.load || !s.load.isHd || s.load === s.presentedLoad) return;
    releaseViewer(s.viewer, s.load);
    s.viewer = s.presentedViewer;
    s.load = s.presentedLoad;
    s.camera = null;
    s.hd = s.presentedLoad && s.presentedLoad.isHd ? 'done' : 'idle';
  }

  function ensureViewer(root, payload, source, isHd) {
    var s = state(root);
    if (s.viewer && s.load && !s.load.failed && !s.load.cancelled && s.load.source === source &&
        String(s.load.payload.variantId) === String(payload.variantId)) {
      if (s.load.loaded) presentViewer(root, s.viewer, s.load);
      return s.viewer;
    }
    // Keep one displayed model and at most one candidate. A return to the displayed variant
    // can reuse it immediately, even while another candidate was loading.
    if (s.viewer && s.viewer !== s.presentedViewer) releaseViewer(s.viewer, s.load);
    if (s.presentedLoad && s.presentedLoad.source === source &&
        String(s.presentedLoad.payload.variantId) === String(payload.variantId)) {
      s.viewer = s.presentedViewer;
      s.load = s.presentedLoad;
      presentViewer(root, s.viewer, s.load);
      return s.viewer;
    }
    var stage = $(root, SELECTORS.stage);
    var load = { payload: payload, source: source, isHd: isHd, loaded: false, failed: false };
    s.load = load;
    var viewer = document.createElement('model-viewer');
    viewer.setAttribute('data-pending', '');
    viewer.setAttribute('inert', '');
    viewer.setAttribute('aria-hidden', 'true');
    viewer.setAttribute('camera-controls', '');
    viewer.setAttribute('max-camera-orbit', 'auto 180deg auto');
    viewer.setAttribute('touch-action', 'none');
    viewer.setAttribute('tone-mapping', 'neutral');
    viewer.setAttribute('environment-image', 'neutral');
    viewer.setAttribute('shadow-intensity', '1');
    viewer.setAttribute('exposure', '1');
    viewer.setAttribute('loading', 'eager');
    viewer.setAttribute('reveal', 'auto');
    viewer.setAttribute('interaction-prompt', 'none'); // the design carries its own hint row
    viewer.setAttribute('alt', '3D model of ' + (payload.productTitle || '') +
      (payload.variantTitle ? ' — ' + payload.variantTitle : ''));
    // The current gallery photo or presented model supplies the loading background.
    function currentLoad() {
      return !load.cancelled && !load.failed && s.viewer === viewer && s.load === load;
    }
    viewer.addEventListener('load', function (event) {
      if (!currentLoad()) return;
      if (isHd && (!isEnabled() || root.getAttribute('data-open') !== 'true' ||
          currentVariantId(root) !== String(payload.variantId))) return;
      if (event.detail && event.detail.url && absoluteUrl(event.detail.url) !== absoluteUrl(load.objectUrl || source)) return;
      if (isHd && !load.committed && Date.now() >= load.expiresAt) { fail(true); return; }
      load.loaded = true;
      if (s.camera) {
        restoreCamera(root, viewer);
        s.camera = null;
      }
      presentViewer(root, viewer, load);
    });
    function fail(timedOut) {
      // model-viewer errors carry no source URL. Each source gets its own element so an old
      // request can never fail a newer variant or mark its 4K upgrade as complete.
      if (!currentLoad()) return;
      load.failed = true;
      window.clearTimeout(load.hdTimer);
      load.hdTimer = null;
      if (isHd) releaseViewer(viewer, load);
      if (currentVariantId(root) !== String(payload.variantId) || !isEnabled() ||
          root.getAttribute('data-open') !== 'true') return;
      if (isHd) {
        delete s.upgraded[String(payload.variantId)];
        s.hd = 'failed';
        s.camera = s.presentedViewer && String(s.presentedLoad.payload.variantId) !== String(payload.variantId)
          ? readCamera(s.presentedViewer) : null;
        ensureViewer(root, payload, payload.glb, false);
        updateHdButton(root, payload);
        var notice = { message: timedOut ? load.policy.message : 'The 4K file could not be loaded.', duration: timedOut ? 10000 : 0 };
        if (s.load.loaded) {
          setLoading(root, false);
        } else s.load.failureNotice = notice;
        setStatus(root, notice.message, notice.duration);
        return;
      }
      setLoading(root, false);
      setStatus(root, 'The 3D model could not be loaded.');
    }
    viewer.addEventListener('error', function () { fail(false); });
    s.viewer = viewer;
    stage.appendChild(viewer);
    if (isHd) {
      // Own the original download so timeout/close can abort the actual response body. The
      // runtime gets only this request's Blob URL; it cannot keep downloading a detached src.
      load.policy = hdLoadPolicy(root);
      load.controller = new AbortController();
      load.expiresAt = Date.now() + load.policy.timeoutMs;
      load.onTimeout = function () { fail(true); };
      load.hdTimer = window.setTimeout(load.onTimeout, load.policy.timeoutMs);
      Promise.resolve().then(function () {
        if (!currentLoad()) return null;
        if (Date.now() >= load.expiresAt) { fail(true); return null; }
        return window.fetch(absoluteUrl(source), { signal: load.controller.signal });
      }).then(function (response) {
        if (!currentLoad()) return null;
        if (!response || !response.ok) throw new Error('4K model download failed');
        return response.blob();
      }).then(function (blob) {
        if (!currentLoad()) return;
        if (Date.now() >= load.expiresAt) { fail(true); return; }
        load.objectUrl = window.URL.createObjectURL(blob);
        ownedModelBlobUrls.add(load.objectUrl);
        viewer.setAttribute('src', load.objectUrl);
      }).catch(function () { fail(false); });
    } else viewer.setAttribute('src', source);
    return viewer;
  }

  function showModel(root, payload) {
    if (!isEnabled() || root.getAttribute('data-open') !== 'true') return;
    var s = state(root);
    var request = ++s.showRequest;
    installModelFetchBypass();
    setLoading(root, true);
    // Products with a configurator profile compose several models in one scene, which
    // <model-viewer> cannot do. Hand the stage to the configurator engine instead.
    if (root.querySelector('[data-product-3d-configurator]') && window.BranchConfigurator) {
      window.BranchConfigurator.open(root, payload);
      return;
    }
    loadRuntime(root).then(function () {
      if (!isEnabled() || request !== s.showRequest || root.getAttribute('data-open') !== 'true' ||
          currentVariantId(root) !== String(payload.variantId)) return;
      var upgraded = !!payload.masterUrl && s.upgraded[String(payload.variantId)] === payload.masterUrl;
      var isHd = canLoadHd() && upgraded;
      var source = isHd ? payload.masterUrl : payload.glb;
      // Carry the visible model's viewpoint into the next variant. Capture from the
      // presented element, since the latest requested element may still be loading.
      if (s.presentedViewer && $(root, SELECTORS.viewer).hasAttribute('data-presented') &&
          String(s.presentedLoad.payload.variantId) !== String(payload.variantId)) {
        captureCamera(root, s.presentedViewer);
      } else if (!s.load || s.load.source !== source) s.camera = null;
      var viewer = ensureViewer(root, payload, source, isHd);
      s.hd = isHd ? (s.load.loaded ? 'done' : 'loading') : 'idle';
      if (typeof viewer.dismissPoster === 'function') viewer.dismissPoster();
      updateHdButton(root, payload);
    }).catch(function (error) {
      if (request !== s.showRequest || root.getAttribute('data-open') !== 'true') return;
      console.error(error);
      setLoading(root, false);
      setStatus(root, 'The 3D viewer could not be loaded.');
    });
  }

  function openViewer(root) {
    if (!isEnabled() || root.getAttribute('data-open') === 'true') return;
    var s = state(root);
    if (!ctaAllowed(s.current) || currentVariantId(root) !== String(s.current.variantId)) return;
    var viewer = $(root, SELECTORS.viewer);
    if (!viewer) return;
    recordUsage();
    clearUnavailable(root);
    captureGalleryImage(root);
    releaseGalleryImages(root, true);
    root.setAttribute('data-open', 'true');
    viewer.removeAttribute('hidden');
    var cta = $(root, SELECTORS.open);
    if (cta) cta.setAttribute('aria-expanded', 'true');
    showModel(root, s.current);
    var close = $(root, SELECTORS.close);
    if (close) close.focus({ preventScroll: true });
  }

  /* Surface used by assets/product-3d-configurator.js. The bypass is exported as an idempotent
     installer, NOT as a fetch function: it works by patching window.fetch, and three's internal
     FileLoader calls the global. Handing back a helper would silently miss those requests. */
  window.BranchProduct3d = {
    isEnabled: isEnabled,
    isAutoOpenEnabled: isAutoOpenEnabled,
    hdLoadPolicy: hdLoadPolicy,
    flushUsage: flushUsage,
    wasUsed: function () { return usageTriggered; },
    ensureFetchBypass: installModelFetchBypass,
    addModelMatcher: function (re) { extraMatchers.push(re); },
    absoluteUrl: absoluteUrl,
    setLoading: setLoading,
    setStatus: setStatus,
    refreshActions: refreshActions,
    prepareAddonPopup: function (root) {
      collapseFullscreen(root, false, false);
      var full = state(root).fullscreen;
      if (!full || !full.animation) return Promise.resolve();
      return full.animation.finished.catch(function () {}).then(function () {
        if (state(root).fullscreen === full) finishFullscreen(root, full, false);
      });
    },
    fetchPayload: fetchPayload,
    formatMb: formatMb,
    canLoadHd: canLoadHd,
  };

  /* Figma annotation on the hint row: "Controls flash for 3 seconds on open". */
  function flashHints(root) {
    root.setAttribute('data-hinting', 'true');
    window.clearTimeout(root.__p3dHintTimer);
    root.__p3dHintTimer = window.setTimeout(function () { root.removeAttribute('data-hinting'); }, 3000);
  }

  function closeViewer(root, restoreFocus) {
    cancelHdCandidate(root);
    state(root).autoOpen = null;
    state(root).qrRequest = (state(root).qrRequest || 0) + 1;
    if (root.__p3dQr) closeQr(root.__p3dQr, false);
    collapseFullscreen(root, true, false);
    clearUnavailable(root);
    state(root).showRequest++;
    var cfg = window.BranchConfigurator && window.BranchConfigurator.get(root);
    if (cfg) cfg.close();
    setLoading(root, false);
    var viewer = $(root, SELECTORS.viewer);
    if (viewer) {
      viewer.setAttribute('hidden', '');
      viewer.removeAttribute('data-presented');
      viewer.removeAttribute('data-actions-ready');
    }
    var placeholder = $(root, SELECTORS.placeholder);
    if (placeholder) placeholder.replaceChildren();
    releaseGalleryImages(root, false);
    root.removeAttribute('data-open');
    root.removeAttribute('data-hinting');
    window.clearTimeout(root.__p3dHintTimer);
    var cta = $(root, SELECTORS.open);
    if (cta) {
      cta.setAttribute('aria-expanded', 'false');
      if (restoreFocus !== false) cta.focus({ preventScroll: true });
    }
    updateVisibility(root);
  }

  // Leaving the page while the browser keeps it in its back/forward cache: close the viewer and
  // free the 3D engine and any plain viewer, so the frozen page does not hold their memory while
  // the shopper browses other products in the same tab. Closing the viewer normally still keeps
  // the scene for an instant reopen. After Back, reopening rebuilds the scene from the selected
  // add-ons; the model files come from the HTTP cache.
  function releaseForBackForwardCache(root) {
    var s = state(root);
    var cfg = window.BranchConfigurator && window.BranchConfigurator.get(root);
    if (root.getAttribute('data-open') === 'true' || s.viewer || (cfg && cfg.active)) {
      closeViewer(root, false);
      releaseViewer(s.viewer, s.load);
      if (s.presentedViewer !== s.viewer) releaseViewer(s.presentedViewer, s.presentedLoad);
      s.viewer = s.presentedViewer = s.load = s.presentedLoad = null;
    }
    if (cfg && cfg.engine && cfg.suspend) cfg.suspend();
  }

  // Policy changes stop and release rendering without removing the shopper's selected addons.
  function disableRoot(root) {
    var s = state(root);
    s.autoOpen = null;
    s.pendingVariant = null;
    var cfg = window.BranchConfigurator && window.BranchConfigurator.get(root);
    if (root.getAttribute('data-open') === 'true' || s.viewer || (cfg && cfg.active)) {
      closeViewer(root, false);
      if (cfg && cfg.suspend) cfg.suspend();
      releaseViewer(s.viewer, s.load);
      if (s.presentedViewer !== s.viewer) releaseViewer(s.presentedViewer, s.presentedLoad);
      s.viewer = s.presentedViewer = s.load = s.presentedLoad = null;
    }
    // A normally closed configurator also retains a scene; drop it when the feature is disabled.
    if (cfg && cfg.engine && cfg.suspend) cfg.suspend();
    if (s.galleryReadinessObserver) s.galleryReadinessObserver.disconnect();
    if (s.galleryReadinessOuter) {
      s.galleryReadinessOuter.removeEventListener('load', s.refreshGalleryVisibility, true);
      s.galleryReadinessOuter.removeEventListener('error', s.refreshGalleryVisibility, true);
    }
    s.galleryReadinessOuter = null;
    updateVisibility(root);
  }

  function selectedAddonStillPresent(detail) {
    var input = document.getElementById('addon-products-for-atc');
    var rows;
    try { rows = JSON.parse((input && input.value) || '[]'); } catch (error) { return false; }
    if (!Array.isArray(rows)) return false;
    if (rows.some(function (row) { return row.productHandle === detail.handle && String(row.variantId) === String(detail.variantId); })) return true;
    var constituents = detail.constituentVariantIds || [];
    var ids = [];
    rows.forEach(function (row) {
      ids.push(String(row.variantId));
      (row.bundleConstituents || []).forEach(function (part) { ids.push(String(part.variantId || part.bundleConstituentId)); });
    });
    return constituents.length > 0 && constituents.every(function (id) { return ids.indexOf(String(id)) !== -1; });
  }

  function tryAutoOpen(root) {
    var s = state(root), intent = s.autoOpen;
    if (!intent || !intent.ready) return;
    if (!isAutoOpenEnabled() || root.getAttribute('data-open') === 'true' ||
        currentVariantId(root) !== intent.variantId || !selectedAddonStillPresent(intent.detail)) {
      s.autoOpen = null; return;
    }
    if (!s.current || String(s.current.variantId) !== intent.variantId) return;
    if (!ctaAllowed(s.current) || s.current.configuratorEnabled === false) { s.autoOpen = null; return; }
    if (!galleryReady(root)) return;
    s.autoOpen = null;
    openViewer(root);
  }

  function autoOpenAfterAddon(event) {
    var detail = event.detail;
    if (!isAutoOpenEnabled() || !detail || detail.checked === false || !detail.variantId || !detail.handle) return;
    var root = roots().filter(function (candidate) { return candidate.getAttribute('data-product-id') === String(detail.productId); })[0];
    if (!root || root.getAttribute('data-open') === 'true') return;
    var configNode = $(root, '[data-product-3d-configurator]'), config;
    try { config = configNode && JSON.parse(configNode.textContent); } catch (error) { return; }
    if (!config || !config.enabled || !(config.accessories || []).some(function (accessory) { return accessory.handle === detail.handle; })) return;
    var intent = { detail: detail, variantId: currentVariantId(root), ready: false };
    var s = state(root);
    s.autoOpen = intent;
    // Let the native cart and any existing closed controller finish this event first.
    // Opening in the same dispatch can reserve the selected accessory a second time.
    window.setTimeout(function () {
      if (s.autoOpen !== intent || !isAutoOpenEnabled()) return;
      intent.ready = true;
      sync(root);
      tryAutoOpen(root);
    }, 0);
  }

  /* ---- fullscreen --------------------------------------------------------------------------- */
  // A nested native dialog enters the top layer without losing the gallery/root ancestry.
  // Move the existing shell synchronously: the loaded model, camera and pending requests survive.
  function fullscreenLabel(root, expanded) {
    var button = $(root, SELECTORS.expand);
    if (!button) return;
    button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    var label = button.getAttribute(expanded ? 'data-label-collapse' : 'data-label-expand');
    if (label) button.setAttribute('aria-label', label);
    var close = $(root, SELECTORS.close);
    if (close) {
      if (!close.__p3dLabel) close.__p3dLabel = close.getAttribute('aria-label');
      close.setAttribute('aria-label', expanded && label ? label : close.__p3dLabel);
    }
  }

  function fullscreenAnimation(full, entering, prepare) {
    var interruptedRect = full.animation && full.dialog.getBoundingClientRect && full.dialog.getBoundingClientRect();
    if (!prepare) clearFullscreenEntry(full);
    if (full.animation) full.animation.cancel();
    full.animation = null;
    if (!full.dialog.animate || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) return null;
    // Grow/shrink the same opaque model between the gallery and viewport rectangles.
    // A fade would expose the gallery photo before the live model was moved back.
    if (!full.root.getBoundingClientRect) return null;
    var galleryRect = full.root.getBoundingClientRect();
    var viewportRect = full.dialog.getBoundingClientRect();
    if (!galleryRect.width || !galleryRect.height) return null;
    var bounds = entering ? [interruptedRect || galleryRect, viewportRect] : [interruptedRect || viewportRect, galleryRect];
    var frames = bounds.map(function (rect) {
      return { opacity: 1, transform: 'none', top: rect.top + 'px', left: rect.left + 'px',
        width: rect.width + 'px', height: rect.height + 'px' };
    });
    full.animation = full.dialog.animate(frames, {
      duration: 280, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards'
    });
    if (prepare) {
      full.animation.pause();
      full.animation.currentTime = 0;
    }
    // Cancelling an interrupted transition rejects its finished promise.
    var animation = full.animation;
    animation.finished.then(function () {
      // Return to viewport units once expanded so rotation/resizing stays responsive.
      if (entering && state(full.root).fullscreen === full && full.animation === animation && !full.closing) {
        clearFullscreenEntry(full);
        full.animation = null;
        animation.cancel();
      }
    }).catch(function () {});
    return full.animation;
  }

  function startFullscreenEntry(full, animation) {
    if (!animation) return;
    function valid() {
      return state(full.root).fullscreen === full && full.animation === animation && !full.closing;
    }
    // Reconnecting model-viewer rebuilds its canvas. Prepare a render before starting
    // the clock, so startup work cannot consume the first half of the grow.
    window.requestAnimationFrame(function () {
      if (!valid()) return;
      window.requestAnimationFrame(function () {
        if (!valid()) return;
        var entry = full.entry;
        if (entry) {
          entry.cover.remove();
          entry.stage.style.opacity = entry.styles.opacity;
          entry.animation.play();
        }
        animation.play();
      });
    });
  }

  function clearFullscreenEntry(full) {
    var entry = full.entry;
    if (!entry) return;
    full.entry = null;
    entry.cover.remove();
    if (entry.animation) entry.animation.cancel();
    if (entry.styles) Object.keys(entry.styles).forEach(function (key) { entry.stage.style[key] = entry.styles[key]; });
  }

  function layoutFullscreenEntry(full) {
    var entry = full.entry;
    if (!entry) return;
    var start = full.root.getBoundingClientRect();
    var end = full.dialog.getBoundingClientRect();
    var ideal = entry.viewer.getIdealAspect();
    function fit(rect) { return Math.min(rect.height, rect.width / ideal); }
    var finalFit = fit(end);
    if (!ideal || !finalFit || !fit(start)) { clearFullscreenEntry(full); return; }
    var stage = entry.stage;
    entry.styles = {};
    ['position', 'left', 'top', 'width', 'height', 'transform', 'transformOrigin', 'opacity'].forEach(function (key) {
      entry.styles[key] = stage.style[key] || '';
    });
    stage.style.position = 'absolute';
    stage.style.left = '50%';
    stage.style.top = '50%';
    stage.style.width = end.width + 'px';
    stage.style.height = end.height + 'px';
    stage.style.transformOrigin = 'center';
    stage.style.opacity = '0';
    function frame(offset) {
      var rect = { width: start.width + (end.width - start.width) * offset,
        height: start.height + (end.height - start.height) * offset };
      return { offset: offset, transform: 'translate(-50%, -50%) scale(' + (fit(rect) / finalFit) + ')' };
    }
    var frames = [frame(0)];
    // Framing switches from height-limited to width-limited at the model's ideal aspect.
    var crossing = (ideal * start.height - start.width) / ((end.width - start.width) - ideal * (end.height - start.height));
    if (crossing > 0 && crossing < 1) frames.push(frame(crossing));
    frames.push(frame(1));
    stage.style.transform = frames[0].transform;
    entry.animation = stage.animate(frames, {
      duration: 280, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards'
    });
    entry.animation.pause();
    entry.animation.currentTime = 0;
    entry.animation.finished.catch(function () {});
    full.shell.appendChild(entry.cover);
  }

  function openFullscreen(full) {
    var root = full.root;
    if (state(root).fullscreen !== full || full.closing) return;
    if (full.entry && full.entry.viewer !== state(root).presentedViewer) clearFullscreenEntry(full);
    holdPresentedCamera(root);
    try { full.dialog.showModal(); }
    catch (error) { finishFullscreen(root, full, false); return; }
    document.body.style.overflow = 'hidden';
    if (document.documentElement) document.documentElement.style.overflow = 'hidden';
    root.setAttribute('data-fullscreen', 'true');
    fullscreenLabel(root, true);
    layoutFullscreenEntry(full);
    var animation = fullscreenAnimation(full, true, true);
    if (!animation) clearFullscreenEntry(full);
    full.dialog.appendChild(full.shell);
    startFullscreenEntry(full, animation);
    $(root, SELECTORS.expand).focus({ preventScroll: true });
  }

  function prepareFullscreenEntry(full) {
    var viewer = state(full.root).presentedViewer;
    var stage = $(full.root, SELECTORS.stage);
    if (!viewer || !stage || !stage.animate || !viewer.toDataURL || !viewer.getIdealAspect ||
        !full.dialog.animate || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
      openFullscreen(full); return;
    }
    try {
      var cover = document.createElement('img');
      cover.alt = '';
      cover.setAttribute('aria-hidden', 'true');
      cover.setAttribute('data-product-3d-entry-cover', '');
      cover.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;max-width:none;margin:0;display:block;object-fit:fill;pointer-events:none;z-index:2';
      cover.src = viewer.toDataURL('image/png');
      full.entry = { viewer: viewer, stage: stage, cover: cover, styles: null, animation: null };
      // Keep the current inline render untouched until its temporary cover is decoded.
      // The live canvas renders once at the final size, then scales with the growing frame.
      Promise.resolve(cover.decode()).catch(function () { clearFullscreenEntry(full); }).then(function () { openFullscreen(full); });
    } catch (error) { clearFullscreenEntry(full); openFullscreen(full); }
  }

  function finishFullscreen(root, full, restoreFocus) {
    var s = state(root);
    if (s.fullscreen !== full) return;
    s.fullscreen = null;
    var qr = root.__p3dQr;
    if (qr && full.dialog.contains(qr)) document.body.appendChild(qr);
    full.shell.removeAttribute('inert');
    if (full.returnCamera && full.returnCamera.viewer === s.presentedViewer) {
      restoreCamera(root, full.returnCamera.viewer, full.returnCamera.camera);
    } else holdPresentedCamera(root);
    clearFullscreenEntry(full);
    if (full.shell.parentElement !== full.parent) full.parent.insertBefore(full.shell, full.dialog);
    if (full.animation) full.animation.cancel();
    full.dialog.close();
    root.removeAttribute('data-fullscreen');
    root.removeAttribute('data-fullscreen-closing');
    document.body.style.overflow = full.bodyOverflow;
    if (document.documentElement) document.documentElement.style.overflow = full.htmlOverflow;
    fullscreenLabel(root, false);
    if (restoreFocus && root.getAttribute('data-open') === 'true') {
      var button = $(root, SELECTORS.expand);
      if (button) button.focus({ preventScroll: true });
    }
  }

  function collapseFullscreen(root, immediate, restoreFocus) {
    var full = state(root).fullscreen;
    if (!full) return;
    if (!full.dialog.open) { finishFullscreen(root, full, restoreFocus !== false); return; }
    if (immediate) { finishFullscreen(root, full, restoreFocus); return; }
    if (full.closing) return;
    full.closing = true;
    full.returnCamera = holdPresentedCamera(root);
    root.setAttribute('data-fullscreen-closing', 'true');
    var animation = fullscreenAnimation(full, false);
    if (!animation) { finishFullscreen(root, full, restoreFocus !== false); return; }
    animation.finished.then(function () {
      if (state(root).fullscreen === full && full.animation === animation && full.closing) {
        finishFullscreen(root, full, restoreFocus !== false);
      }
    }).catch(function () {});
  }

  function toggleFullscreen(root) {
    if (!isEnabled()) return;
    var s = state(root);
    if (s.fullscreen) {
      if (s.fullscreen.closing) {
        s.fullscreen.closing = false;
        s.fullscreen.returnCamera = null;
        root.removeAttribute('data-fullscreen-closing');
        fullscreenAnimation(s.fullscreen, true);
      } else collapseFullscreen(root, false, true);
      return;
    }
    if (root.getAttribute('data-open') !== 'true') return;
    var dialog = $(root, SELECTORS.fullscreen);
    var shell = $(root, SELECTORS.viewer);
    if (!dialog || !shell || typeof dialog.showModal !== 'function') return;
    var full = {
      root: root, dialog: dialog, shell: shell, parent: shell.parentElement,
      bodyOverflow: document.body.style.overflow,
      htmlOverflow: document.documentElement && document.documentElement.style.overflow,
      closing: false, animation: null
    };
    s.fullscreen = full;
    prepareFullscreenEntry(full);
  }

  /* ---- camera continuity and 4K master ------------------------------------------------------- */
  function readCamera(viewer) {
    try {
      var fov = typeof viewer.getFieldOfView === 'function' ? viewer.getFieldOfView() : null;
      var projection = typeof viewer.fieldOfView === 'string' ? viewer.fieldOfView : null;
      // Normalize the LIVE zoom against the current viewport. The fieldOfView property
      // alone omits wheel/pinch zoom; getFieldOfView alone includes portrait compensation.
      if (fov && typeof viewer.getIdealAspect === 'function' && viewer.clientWidth && viewer.clientHeight) {
        var adjustment = Math.max(1, viewer.getIdealAspect() / (viewer.clientWidth / viewer.clientHeight));
        projection = (2 * Math.atan(Math.tan(fov * Math.PI / 360) / adjustment) * 180 / Math.PI) + 'deg';
      }
      return {
        orbit: String(viewer.getCameraOrbit()),
        target: viewer.getCameraTarget(),
        center: typeof viewer.getBoundingBoxCenter === 'function' ? viewer.getBoundingBoxCenter() : null,
        projection: projection,
        fov: fov
      };
    } catch (e) { return null; }
  }

  function captureCamera(root, viewer) {
    state(root).camera = readCamera(viewer);
  }

  function holdPresentedCamera(root) {
    var viewer = state(root).presentedViewer;
    if (!viewer) return;
    // Drag/pinch changes the live controls, not the camera attributes. Pin their current
    // values before reconnecting/resizing so model-viewer cannot reapply its initial view.
    // Keep this separate from the camera snapshot belonging to a pending variant/4K load.
    var camera = readCamera(viewer);
    if (camera) restoreCamera(root, viewer, camera);
    return camera ? { viewer: viewer, camera: camera } : null;
  }

  /* Keep orbit + zoom (FOV) across the swap; re-aim at the NEW model's center carrying any pan as
     an offset — never restore the absolute target, the files may have different origins. */
  function restoreCamera(root, viewer, camera) {
    var cam = camera || state(root).camera;
    if (!cam) return;
    try {
      viewer.setAttribute('camera-orbit', cam.orbit);
      // getFieldOfView includes the aspect-ratio adjustment. Reusing that as the authored
      // FOV would change zoom on each square ↔ portrait/fullscreen transition.
      if (cam.projection) viewer.setAttribute('field-of-view', cam.projection);
      else if (cam.fov) viewer.setAttribute('field-of-view', cam.fov + 'deg');
      if (cam.target && cam.center && typeof viewer.getBoundingBoxCenter === 'function') {
        var c = viewer.getBoundingBoxCenter();
        viewer.setAttribute('camera-target',
          (c.x + cam.target.x - cam.center.x).toFixed(4) + 'm ' +
          (c.y + cam.target.y - cam.center.y).toFixed(4) + 'm ' +
          (c.z + cam.target.z - cam.center.z).toFixed(4) + 'm');
      } else {
        viewer.removeAttribute('camera-target');
      }
      if (typeof viewer.jumpCameraToGoal === 'function') viewer.jumpCameraToGoal();
    } catch (e) { viewer.removeAttribute('camera-target'); }
  }

  function loadHd(root) {
    if (!canLoadHd() || !actionsReady(root)) return;
    // The configurator owns its own scene, so it performs the master swap itself.
    if (root.querySelector('[data-product-3d-configurator]') && window.BranchConfigurator) {
      var cfg = window.BranchConfigurator.get(root);
      if (cfg) { cfg.loadHd(); return; }
    }
    var s = state(root);
    var payload = s.current;
    if (!actionsReady(root)) return;
    if (!payload || !payload.masterUrl || !s.presentedViewer || s.hd === 'loading' || s.hd === 'done' ||
        currentVariantId(root) !== String(payload.variantId)) return;
    if (String(s.presentedLoad.payload.variantId) !== String(payload.variantId)) return;
    s.hd = 'loading';
    captureCamera(root, s.presentedViewer);
    updateHdButton(root, payload);
    setLoading(root, true);
    ensureViewer(root, payload, payload.masterUrl, true);
  }

  /* ---- AR ------------------------------------------------------------------------------------ */
  function quickLookSupported() {
    var probe = document.createElement('a');
    return !!((probe.relList && probe.relList.supports && probe.relList.supports('ar')) ||
      (/iPad|iPhone|iPod/.test(navigator.userAgent) && window.webkit && window.webkit.messageHandlers));
  }
  function isAndroid() { return /android/i.test(navigator.userAgent); }

  function launchNativeAr(root, handoff) {
    // A QR handoff may launch native AR even without an experiment assignment on the phone.
    if (!isEnabled() && !(handoff && new URLSearchParams(window.location.search).get('ar') === '1')) return false;
    var payload = state(root).current;
    if (!ctaAllowed(payload) || currentVariantId(root) !== String(payload.variantId)) return false;
    if (quickLookSupported() && payload.usdz) {
      var anchor = document.createElement('a');
      anchor.setAttribute('rel', 'ar');
      var img = document.createElement('img');
      img.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
      anchor.appendChild(img);
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.setAttribute('href', absoluteUrl(payload.usdz));
      recordUsage();
      anchor.click();
      window.setTimeout(function () { anchor.remove(); }, 5000);
      return true;
    }
    if (isAndroid() && payload.glb) {
      recordUsage();
      var fallback = window.location.origin + window.location.pathname + '?variant=' + encodeURIComponent(payload.variantId);
      window.location.href = 'intent://arvr.google.com/scene-viewer/1.0?file=' + encodeURIComponent(absoluteUrl(payload.glb)) +
        '&mode=ar_preferred&title=' + encodeURIComponent(payload.productTitle || document.title) +
        '#Intent;scheme=https;package=com.google.android.googlequicksearchbox;action=android.intent.action.VIEW;' +
        'S.browser_fallback_url=' + encodeURIComponent(fallback) + ';end;';
      return true;
    }
    return false;
  }

  /* ---- QR (desktop → phone handoff) ---------------------------------------------------------- */
  var qrLibPromise = null;
  function loadQrLib(root) {
    if (window.QRCode) return Promise.resolve();
    if (qrLibPromise) return qrLibPromise;
    qrLibPromise = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = root.getAttribute('data-qrcode-lib') || '';
      script.onload = function () { resolve(); };
      script.onerror = function () { reject(new Error('[product-3d] QR library failed to load')); };
      document.head.appendChild(script);
    });
    return qrLibPromise;
  }

  function arHandoffUrl(root) {
    var params = new URLSearchParams();
    var variantId = (state(root).current && state(root).current.variantId) || currentVariantId(root);
    if (variantId) params.set('variant', variantId);
    params.set('ar', '1');
    /* Unpublished themes: the phone must land on the same theme. Shopify strips preview_theme_id
       from location.search after the first navigation, but window.Shopify.theme knows the renderer. */
    var theme = window.Shopify && window.Shopify.theme;
    if (theme && theme.id && theme.role !== 'main') params.set('preview_theme_id', theme.id);
    return window.location.origin + window.location.pathname + '?' + params.toString();
  }

  function openQr(root) {
    if (!isEnabled()) return;
    var modal = root.__p3dQr || $(root, SELECTORS.qr);
    if (!modal) return;
    root.__p3dQr = modal;
    refreshActions(root);
    var s = state(root);
    var request = s.qrRequest = (s.qrRequest || 0) + 1;
    loadQrLib(root).then(function () {
      if (s.qrRequest !== request || !actionsReady(root)) return;
      // A body popup is behind the fullscreen top layer. Choose its host when the library
      // resolves, since fullscreen can be entered or exited while the QR script downloads.
      var full = state(root).fullscreen;
      var host = full ? full.dialog : document.body;
      if (modal.parentElement !== host) host.appendChild(modal);
      if (full) full.shell.setAttribute('inert', '');
      var target = $(modal, SELECTORS.qrCode);
      var url = arHandoffUrl(root);
      target.innerHTML = '';
      target.setAttribute('data-encoded-url', url);
      new window.QRCode(target, { text: url, width: 230, height: 230, correctLevel: window.QRCode.CorrectLevel ? window.QRCode.CorrectLevel.M : undefined });
      modal.removeAttribute('hidden');
      var close = $(modal, SELECTORS.qrClose);
      if (close) close.focus({ preventScroll: true });
    }).catch(function (error) { console.error(error); });
  }
  function closeQr(modal, restoreFocus) {
    modal.setAttribute('hidden', '');
    roots().some(function (root) {
      if (root.__p3dQr !== modal) return false;
      var shell = $(root, SELECTORS.viewer);
      if (shell) shell.removeAttribute('inert');
      var button = $(root, SELECTORS.ar);
      if (restoreFocus !== false && button && root.getAttribute('data-open') === 'true') button.focus({ preventScroll: true });
      return true;
    });
  }

  /* ---- events -------------------------------------------------------------------------------- */
  document.addEventListener('click', function (event) {
    // The gallery also clicks thumbnails programmatically while restoring variants. Only a
    // shopper's click/keyboard activation should leave 3D; let the gallery handle the image.
    if (!event.isTrusted) return;
    var thumbnail = event.target.closest('.main-product-gallery__thumbnail');
    var gallery = thumbnail && thumbnail.closest('.main-product-gallery');
    var root = gallery && $(gallery, SELECTORS.root);
    if (root && root.getAttribute('data-open') === 'true') closeViewer(root, false);
  }, true);

  document.addEventListener('click', function (event) {
    var target = event.target;
    var openTrigger = target.closest(SELECTORS.open);
    if (openTrigger) { var r1 = openTrigger.closest(SELECTORS.root); if (r1) openViewer(r1); return; }
    var closeTrigger = target.closest(SELECTORS.close);
    if (closeTrigger) {
      var r2 = closeTrigger.closest(SELECTORS.root);
      if (r2) {
        if (state(r2).fullscreen) collapseFullscreen(r2, false, true);
        else closeViewer(r2);
      }
      return;
    }
    var expandTrigger = target.closest(SELECTORS.expand);
    if (expandTrigger) { var expandedRoot = expandTrigger.closest(SELECTORS.root); if (expandedRoot) toggleFullscreen(expandedRoot); return; }
    var hdTrigger = target.closest(SELECTORS.hd);
    if (hdTrigger) { var r3 = hdTrigger.closest(SELECTORS.root); if (r3) loadHd(r3); return; }
    var arTrigger = target.closest(SELECTORS.ar);
    if (arTrigger) {
      var r4 = arTrigger.closest(SELECTORS.root);
      if (r4 && !actionsReady(r4)) return;
      if (r4 && (!ctaAllowed(state(r4).current) || currentVariantId(r4) !== String(state(r4).current.variantId))) return;
      if (r4 && !launchNativeAr(r4)) { recordUsage(); openQr(r4); }
      return;
    }
    var qrModal = target.closest(SELECTORS.qr);
    if (qrModal && (target === qrModal || target.closest(SELECTORS.qrClose))) closeQr(qrModal);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var openQrModal = document.querySelector(SELECTORS.qr + ':not([hidden])');
    if (openQrModal) { event.preventDefault(); closeQr(openQrModal); return; }
    var expandedRoot = roots().filter(function (root) { return !!state(root).fullscreen; })[0];
    if (expandedRoot) { event.preventDefault(); collapseFullscreen(expandedRoot, false, true); return; }
    roots().forEach(function (root) { if (root.getAttribute('data-open') === 'true') closeViewer(root); });
  });

  /* The PDP exposes the selection through the swatch radios (option-0, option-1, …); the theme's
     own JS updates the hidden id[] input right after, so read it on the next frame. */
  document.addEventListener('change', function (event) {
    var name = event.target && event.target.name;
    if (!name || name.indexOf('option-') !== 0) return;
    window.requestAnimationFrame(function () { window.setTimeout(syncAll, 0); });
  });
  document.addEventListener('galleryHydrated', syncAll);
  document.addEventListener('branch:addon:selected', autoOpenAfterAddon);
  document.addEventListener('branch:addon:toggle', autoOpenAfterAddon);
  if (window.jQuery) {
    window.jQuery(document).on('variantChange.product3d', '.main-product__item[data-quick-view="false"]', syncAll);
  }
  window.setTimeout(syncAll, 1500);
  window.addEventListener('popstate', syncAll);

  var mobileHdMedia = window.matchMedia && window.matchMedia(mobileHdQuery);
  function refreshHdAvailability() {
    roots().forEach(function (root) {
      var cfg = window.BranchConfigurator && window.BranchConfigurator.get(root);
      var s = state(root);
      if (!cfg && !canLoadHd() && s.load && s.load.isHd && s.load !== s.presentedLoad) {
        cancelHdCandidate(root);
        if (isEnabled() && root.getAttribute('data-open') === 'true' && s.current &&
            currentVariantId(root) === String(s.current.variantId)) showModel(root, s.current);
      }
      if (cfg && cfg.syncHdButton) cfg.syncHdButton();
      else updateHdButton(root, state(root).current);
      refreshActions(root);
    });
  }
  if (mobileHdMedia) {
    if (mobileHdMedia.addEventListener) mobileHdMedia.addEventListener('change', refreshHdAvailability);
    else if (mobileHdMedia.addListener) mobileHdMedia.addListener(refreshHdAvailability);
  }

  /* ---- boot ---------------------------------------------------------------------------------- */
  roots().forEach(function (root) {
    state(root);
    if (isEnabled()) observeGalleryReadiness(root);
    updateVisibility(root);
    var dialog = $(root, SELECTORS.fullscreen);
    if (dialog) dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      var qr = root.__p3dQr;
      if (qr && !qr.hasAttribute('hidden')) closeQr(qr);
      else collapseFullscreen(root, false, true);
    });
  });

  var observedExperiment = null, policyKey = null;
  var experimentObserver = new MutationObserver(refreshPolicy);
  function refreshPolicy() {
    var node = document.getElementById(experimentId);
    if (node !== observedExperiment) {
      experimentObserver.disconnect();
      observedExperiment = node;
      if (node) experimentObserver.observe(node, { attributes: true, attributeFilter: ['data-test-active', 'data-test-variant'] });
      watchExperimentMount();
    }
    var key = experimentMode() + ':' + isEnabled() + ':' + isAutoOpenEnabled();
    if (policyKey === key) return;
    policyKey = key;
    roots().forEach(function (root) {
      if (!isAutoOpenEnabled()) state(root).autoOpen = null;
      if (isEnabled()) sync(root); else disableRoot(root);
    });
  }
  // Also support Visually replacing/inserting the flag; ignore unrelated DOM mutations.
  // Once the flag exists, only its parent is watched for a replacement: a page-wide subtree
  // observer receives every slider, image and app mutation on the page (~900 records a second
  // on a phone PDP). Until the flag exists, the page is watched for its insertion.
  var experimentMountObserver = new MutationObserver(function () {
    if (document.getElementById(experimentId) !== observedExperiment) refreshPolicy();
  });
  function watchExperimentMount() {
    experimentMountObserver.disconnect();
    var parent = observedExperiment && observedExperiment.parentElement;
    var target = parent || document.body;
    if (target) experimentMountObserver.observe(target, { childList: true, subtree: !parent });
  }
  watchExperimentMount();
  if (desktopMedia) {
    if (desktopMedia.addEventListener) desktopMedia.addEventListener('change', refreshPolicy);
    else if (desktopMedia.addListener) desktopMedia.addListener(refreshPolicy);
  }
  refreshPolicy();
  // persisted is true only when the page is going into the back/forward cache, not when it unloads.
  window.addEventListener('pagehide', function (event) {
    if (event && event.persisted) roots().forEach(releaseForBackForwardCache);
  });

  /* ?ar=1 — native AR first, even without a Visually assignment on the phone. Only an
     enabled experience may fall back to the web viewer when native AR is absent. */
  function autoLaunch() {
    if (new URLSearchParams(window.location.search).get('ar') !== '1') return;
    var root = roots()[0];
    if (!root || !ctaAllowed(state(root).current)) return;
    root.scrollIntoView({ block: 'center' });
    if (!launchNativeAr(root, true) && isEnabled()) openViewer(root);
  }
  // The configurator's deferred module follows this one. Wait until all deferred modules
  // are installed before an AR handoff falls back to opening the 3D renderer.
  if (document.readyState === 'loading' || document.readyState === 'interactive') {
    document.addEventListener('DOMContentLoaded', autoLaunch, { once: true });
  } else autoLaunch();
})();
